import { FC, useState, useEffect } from 'react'
import { Card, InputNumber, Button, message } from 'antd'
import { SaveOutlined } from '@ant-design/icons'

// CSS for financial input styling
const financialInputStyles = `
  .financial-input .ant-input-number-input {
    text-align: right !important;
  }
`

interface RemainingCashInputProps {
  value: number
  onChange: (amount: number) => Promise<boolean>
  loading?: boolean
}

export const RemainingCashInput: FC<RemainingCashInputProps> = ({
  value,
  onChange,
  loading = false
}) => {
  const [localValue, setLocalValue] = useState<number>(value)
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Update local value when prop value changes
  useEffect(() => {
    setLocalValue(value)
    setHasChanges(false)
  }, [value])

  // Handle input change
  const handleInputChange = (newValue: number | null) => {
    const amount = newValue || 0
    if (amount < 0) {
      message.warning('จำนวนเงินสดคงเหลือต้องไม่เป็นลบ')
      return
    }
    
    setLocalValue(amount)
    setHasChanges(amount !== value)
  }

  // Handle save
  const handleSave = async () => {
    if (!hasChanges) {
      message.info('ไม่มีการเปลี่ยนแปลงที่ต้องบันทึก')
      return
    }

    setSaving(true)
    try {
      const success = await onChange(localValue)
      if (success) {
        message.success('บันทึกเงินสดคงเหลือสำเร็จ')
        setHasChanges(false)
      } else {
        message.error('เกิดข้อผิดพลาดในการบันทึก')
      }
    } catch (error) {
      message.error('เกิดข้อผิดพลาดในการบันทึก')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: financialInputStyles }} />
      <Card 
      title={
        <div className="flex items-center">
          <span className="font-medium">เงินสดคงเหลือ</span>
        </div>
      }
      loading={loading}
      className="h-full"
    >
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="font-semibold text-lg">เหลือเงินสด</span>
            <InputNumber
              value={localValue === 0 ? null : localValue}
              onChange={handleInputChange}
              placeholder="0"
              min={0}
              max={10000000}
              step={0.01}
              precision={2}
              size="large"
              className="financial-input"
              style={{ width: '200px' }}
              addonBefore="฿"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
              disabled={loading || saving}
              onKeyDown={(e) => {
                // Allow only numbers, decimal point, and control keys
                if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                  e.preventDefault()
                }
              }}
            />
            <span className="font-semibold text-lg">บาท</span>
          </div>
        </div>

        <div className="space-y-2">
          <Button
            type="primary"
            block
            onClick={handleSave}
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!hasChanges || loading}
            size="middle"
          >
            Save
          </Button>
        </div>
      </div>
    </Card>
    </>
  )
}